import React, { useState } from 'react';
import {
  SquareDashed,
  Brush,
  DoorOpen,
  AppWindow,
  Eraser,
  BrickWall,
  RefreshCw,
  Trash2,
  Sparkles,
  Wand2,
  Loader2,
  Download,
  Bath,
  Droplets,
  Flame,
  CircleDot,
  ChevronDown,
  ChevronUp,
  ShowerHead,
  Type,
  Undo2,
  Redo2,
} from 'lucide-react';
import { 
  AmendmentType as SharedAmendmentType, 
  SelectorTool as SharedSelectorTool 
} from '../types';

// ============================================================================
// TYPES
// ============================================================================

// Re-export from shared types for backwards compatibility
export type SelectorTool = SharedSelectorTool;
export type AmendmentType = SharedAmendmentType;

export interface AmendmentAction {
  type: AmendmentType;
  prompt: string;
  icon: React.ElementType;
  label: string;
  color: 'blue' | 'green' | 'amber' | 'red';
}

export interface EditorRightSidebarProps {
  /** Currently active selection tool */
  activeTool: SelectorTool;
  /** Callback when tool changes */
  onToolChange: (tool: SelectorTool) => void;
  /** Whether a selection has been made */
  hasSelection: boolean;
  /** Callback when an amendment action is clicked */
  onAmendmentClick: (action: AmendmentAction) => void;
  /** Callback when custom prompt is submitted */
  onCustomPromptSubmit: (prompt: string) => void;
  /** Callback when Update Plan is clicked */
  onUpdatePlan: () => void;
  /** Callback when Download is clicked */
  onDownload: () => void;
  /** Callback when text is added */
  onAddText: (text: string, fontSize: number) => void;
  /** Callback for undo */
  onUndo?: () => void;
  /** Callback for redo */
  onRedo?: () => void;
  /** Whether undo is available */
  canUndo?: boolean;
  /** Whether redo is available */
  canRedo?: boolean;
  /** Whether an update is in progress */
  isProcessing?: boolean;
  /** Optional className */
  className?: string;
}

// ============================================================================
// AMENDMENT ACTIONS CONFIG
// Short, focused prompts - the Edge Function adds style prefix/suffix
// ============================================================================

// Basic structural elements
const STRUCTURAL_ACTIONS: AmendmentAction[] = [
  {
    type: 'addDoorLeft',
    prompt: 'Add left swing door',
    icon: DoorOpen,
    label: 'Door ↰',
    color: 'blue',
  },
  {
    type: 'addDoorRight',
    prompt: 'Add right swing door',
    icon: DoorOpen,
    label: 'Door ↱',
    color: 'blue',
  },
  {
    type: 'flipSwing',
    prompt: 'Flip door swing direction',
    icon: RefreshCw,
    label: 'Flip',
    color: 'amber',
  },
  {
    type: 'addWindow',
    prompt: 'Add window symbol',
    icon: AppWindow,
    label: 'Window',
    color: 'blue',
  },
  {
    type: 'buildWall',
    prompt: 'Solid black wall',
    icon: BrickWall,
    label: 'Wall',
    color: 'green',
  },
  {
    type: 'clearObject',
    prompt: 'Clear area',
    icon: Trash2,
    label: 'Clear',
    color: 'red',
  },
];

// Bathroom fixtures
const BATHROOM_ACTIONS: AmendmentAction[] = [
  {
    type: 'addToilet',
    prompt: 'Add toilet symbol',
    icon: CircleDot,
    label: 'Toilet',
    color: 'blue',
  },
  {
    type: 'addSink',
    prompt: 'Add sink symbol',
    icon: Droplets,
    label: 'Sink',
    color: 'blue',
  },
  {
    type: 'addBathtub',
    prompt: 'Add bathtub symbol',
    icon: Bath,
    label: 'Bath',
    color: 'blue',
  },
  {
    type: 'addShower',
    prompt: 'Add shower cubicle symbol',
    icon: ShowerHead,
    label: 'Shower',
    color: 'blue',
  },
];

// Kitchen fixtures
const KITCHEN_ACTIONS: AmendmentAction[] = [
  {
    type: 'addStove',
    prompt: 'Add stove symbol',
    icon: Flame,
    label: 'Stove',
    color: 'amber',
  },
  {
    type: 'addKitchenSink',
    prompt: 'Add kitchen sink symbol',
    icon: Droplets,
    label: 'K.Sink',
    color: 'blue',
  },
];

// Combined for backwards compatibility
const AMENDMENT_ACTIONS: AmendmentAction[] = [
  ...STRUCTURAL_ACTIONS,
  ...BATHROOM_ACTIONS,
  ...KITCHEN_ACTIONS,
];

// ============================================================================
// SELECTOR TOOL BUTTON
// ============================================================================

interface ToolButtonProps {
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const ToolButton: React.FC<ToolButtonProps> = ({
  icon: Icon,
  label,
  isActive,
  onClick,
  disabled = false,
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`
      flex-1 p-4 rounded-xl text-center transition-all border-2 group
      ${isActive
        ? 'bg-teal-900/40 border-teal-500 text-teal-300 shadow-lg shadow-teal-900/20'
        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 hover:bg-slate-800 text-slate-300'
      }
      disabled:opacity-50 disabled:cursor-not-allowed
    `}
  >
    <div className="flex flex-col items-center gap-2">
      <div className={`p-3 rounded-lg ${isActive ? 'bg-teal-900/50' : 'bg-slate-700 group-hover:bg-slate-600'}`}>
        <Icon size={22} className={isActive ? 'text-teal-400' : 'text-slate-400 group-hover:text-slate-300'} />
      </div>
      <span className="text-sm font-medium">{label}</span>
    </div>
    {isActive && (
      <div className="mt-2 flex items-center justify-center gap-1">
        <div className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
        <span className="text-xs text-teal-400">Active</span>
      </div>
    )}
  </button>
);

// ============================================================================
// AMENDMENT ACTION BUTTON
// ============================================================================

interface ActionButtonProps {
  action: AmendmentAction;
  onClick: () => void;
  disabled?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  action,
  onClick,
  disabled = false,
}) => {
  const Icon = action.icon;
  
  const colorClasses = {
    blue: 'hover:border-blue-500/50 hover:bg-blue-900/20 hover:text-blue-300',
    green: 'hover:border-green-500/50 hover:bg-green-900/20 hover:text-green-300',
    amber: 'hover:border-amber-500/50 hover:bg-amber-900/20 hover:text-amber-300',
    red: 'hover:border-red-500/50 hover:bg-red-900/20 hover:text-red-300',
  };

  const iconColors = {
    blue: 'group-hover:text-blue-400',
    green: 'group-hover:text-green-400',
    amber: 'group-hover:text-amber-400',
    red: 'group-hover:text-red-400',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        p-3 rounded-xl text-center transition-all border border-slate-700 bg-slate-800/50 text-slate-300 group
        ${colorClasses[action.color]}
        disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-slate-700 disabled:hover:bg-slate-800/50 disabled:hover:text-slate-300
      `}
      title={disabled ? 'Select an area first' : action.label}
    >
      <div className="flex flex-col items-center gap-1.5">
        <Icon size={20} className={`text-slate-400 ${disabled ? '' : iconColors[action.color]}`} />
        <span className="text-xs font-medium">{action.label}</span>
      </div>
    </button>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const EditorRightSidebar: React.FC<EditorRightSidebarProps> = ({
  activeTool,
  onToolChange,
  hasSelection,
  onAmendmentClick,
  onCustomPromptSubmit,
  onUpdatePlan,
  onDownload,
  onAddText,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  isProcessing = false,
  className = '',
}) => {
  const [customPrompt, setCustomPrompt] = useState('');
  const [textInput, setTextInput] = useState('');
  const [fontSize, setFontSize] = useState(24);

  const handleCustomSubmit = () => {
    if (customPrompt.trim()) {
      onCustomPromptSubmit(customPrompt.trim());
      setCustomPrompt('');
    }
  };

  const handleAddText = () => {
    if (textInput.trim() && hasSelection) {
      onAddText(textInput.trim(), fontSize);
      setTextInput('');
    }
  };

  const handleTextKeyDown = (e: React.KeyboardEvent) => {
    // Shift+Enter = new line, Enter = submit
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddText();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCustomSubmit();
    }
  };

  return (
    <div className={`w-80 bg-slate-900 border-l border-slate-800 flex flex-col shadow-xl ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-slate-800 bg-gradient-to-r from-slate-900 to-slate-800">
        <h2 className="font-bold text-teal-300 text-lg flex items-center gap-2">
          <Wand2 size={20} className="text-teal-400" />
          AI Editor
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Select an area, then choose an action
        </p>
      </div>

      {/* Undo/Redo Toolbar */}
      {(onUndo || onRedo) && (
        <div className="flex gap-2 px-4 py-2 border-b border-slate-800 bg-slate-900/50">
          <button
            onClick={onUndo}
            disabled={!canUndo || isProcessing}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
              bg-slate-800 border border-slate-700 text-slate-300
              hover:bg-slate-700 hover:text-white
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-800 disabled:hover:text-slate-300"
            title="Undo (Ctrl+Z)"
          >
            <Undo2 size={16} />
            Undo
          </button>
          <button
            onClick={onRedo}
            disabled={!canRedo || isProcessing}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
              bg-slate-800 border border-slate-700 text-slate-300
              hover:bg-slate-700 hover:text-white
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-800 disabled:hover:text-slate-300"
            title="Redo (Ctrl+Y)"
          >
            <Redo2 size={16} />
            Redo
          </button>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Section A: Selector Tools */}
        <div className="p-4 border-b border-slate-800">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <SquareDashed size={14} />
            Selection Tools
          </h3>
          
          <div className="flex gap-3">
            <ToolButton
              icon={SquareDashed}
              label="Box Select"
              isActive={activeTool === 'box'}
              onClick={() => onToolChange(activeTool === 'box' ? null : 'box')}
              disabled={isProcessing}
            />
            <ToolButton
              icon={Brush}
              label="Brush Mask"
              isActive={activeTool === 'brush'}
              onClick={() => onToolChange(activeTool === 'brush' ? null : 'brush')}
              disabled={isProcessing}
            />
          </div>

          {/* Selection status */}
          {activeTool && !hasSelection && (
            <div className="mt-3 p-2 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-400 text-center">
                {activeTool === 'box'
                  ? 'Click and drag on the image to select an area'
                  : 'Paint over the area you want to modify'
                }
              </p>
            </div>
          )}

          {hasSelection && (
            <div className="mt-3 p-2 bg-teal-900/30 rounded-lg border border-teal-700/50">
              <p className="text-xs text-teal-300 text-center flex items-center justify-center gap-1">
                <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                Area selected - choose an action below
              </p>
            </div>
          )}
        </div>

        {/* Section B: Structural Elements */}
        <div className="p-4 border-b border-slate-800">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <BrickWall size={14} className="text-teal-400" />
            Structural
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            {STRUCTURAL_ACTIONS.map((action) => (
              <ActionButton
                key={action.type}
                action={action}
                onClick={() => onAmendmentClick(action)}
                disabled={!hasSelection || isProcessing}
              />
            ))}
          </div>
        </div>

        {/* Section: Text Labels */}
        <div className="p-4 border-b border-slate-800">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Type size={14} className="text-purple-400" />
            Text Label
          </h3>
          
          {/* Text input */}
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={handleTextKeyDown}
            placeholder="Type text (Shift+Enter for new line)"
            disabled={isProcessing || !hasSelection}
            rows={2}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 disabled:opacity-50 resize-none mb-2"
          />
          
          {/* Font size slider */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-slate-400">Font Size</label>
              <span className="text-xs text-slate-300 font-medium">{fontSize}px</span>
            </div>
            <input
              type="range"
              min="12"
              max="72"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              disabled={isProcessing || !hasSelection}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-500 disabled:opacity-50"
            />
          </div>
          
          {/* Add button */}
          <button
            onClick={handleAddText}
            disabled={!textInput.trim() || !hasSelection || isProcessing}
            className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Add Text
          </button>
          
          {!hasSelection && (
            <p className="text-xs text-slate-500 mt-2">
              Select an area to place text
            </p>
          )}
          
          {hasSelection && (
            <p className="text-xs text-slate-400 mt-2">
              Shift+Enter for new line, Enter to add
            </p>
          )}
        </div>

        {/* Section: Bathroom Fixtures */}
        <div className="p-4 border-b border-slate-800">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Bath size={14} className="text-blue-400" />
            Bathroom
          </h3>
          <div className="grid grid-cols-4 gap-1.5">
            {BATHROOM_ACTIONS.map((action) => (
              <ActionButton
                key={action.type}
                action={action}
                onClick={() => onAmendmentClick(action)}
                disabled={!hasSelection || isProcessing}
              />
            ))}
          </div>
        </div>

        {/* Section D: Kitchen Fixtures */}
        <div className="p-4 border-b border-slate-800">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Flame size={14} className="text-amber-400" />
            Kitchen
          </h3>
          <div className="grid grid-cols-2 gap-1.5">
            {KITCHEN_ACTIONS.map((action) => (
              <ActionButton
                key={action.type}
                action={action}
                onClick={() => onAmendmentClick(action)}
                disabled={!hasSelection || isProcessing}
              />
            ))}
          </div>
          
          {!hasSelection && (
            <p className="text-xs text-slate-500 text-center mt-3">
              Select an area first to enable actions
            </p>
          )}
        </div>

        {/* Section C: Custom Request */}
        <div className="p-4">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Sparkles size={14} className="text-purple-400" />
            Custom Request
          </h3>

          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe a custom change (e.g., 'Change floor to wood texture', 'Add a kitchen island')"
            disabled={isProcessing}
            className="w-full h-20 px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 resize-none disabled:opacity-50"
          />

          <p className="text-xs text-slate-500 mt-2">
            Works with or without a selection
          </p>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="p-4 border-t border-slate-800 bg-gradient-to-t from-slate-900 to-slate-900/95 space-y-3">
        {/* Update Plan Button - FREE */}
        <button
          onClick={onUpdatePlan}
          disabled={isProcessing || (!hasSelection && !customPrompt.trim())}
          className={`
            w-full py-3.5 rounded-xl font-semibold text-base flex items-center justify-center gap-2 transition-all
            ${isProcessing || (!hasSelection && !customPrompt.trim())
              ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 text-white shadow-lg shadow-teal-900/30 hover:shadow-teal-900/50 active:scale-[0.98]'
            }
          `}
        >
          {isProcessing ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Wand2 size={20} />
              Update Plan
              <span className="text-xs bg-teal-700/50 px-2 py-0.5 rounded-full ml-1">FREE</span>
            </>
          )}
        </button>

        {/* Download Button */}
        <button
          onClick={onDownload}
          disabled={isProcessing}
          className="w-full py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download size={18} />
          Download Floor Plan
        </button>
      </div>
    </div>
  );
};

export default EditorRightSidebar;
